import React, { useState } from 'react';
import {
  Box, Chip, IconButton, Popover, Tooltip, Typography, List, ListItem, ListItemText, CircularProgress,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import useDocumentSummaries from '../../hooks/useDocumentSummaries';

/**
 * T3-129: normalise a payment row's proof link into an id array.
 *
 * `proof_document_ids` (INTEGER[]) is canonical. `proof_of_payment_document` is the retained
 * derived mirror of [1] and is still populated, so it is the fallback for any row a reader
 * reaches before the array is available.
 *
 * ⚠ The mirror column held a FILENAME before T3-105 (a few legacy rows may still), so the
 * Number() is guarded — a non-numeric legacy value yields no attachment rather than a NaN id
 * that would 404 on click.
 *
 * @param {Object} payment - a payment row from fn_payment_get_list / _get_by_id / _wht_get_matched
 * @returns {number[]}
 */
export const toProofIds = (payment) => {
  if (Array.isArray(payment?.proof_document_ids) && payment.proof_document_ids.length > 0) {
    return payment.proof_document_ids.map(Number).filter((n) => Number.isFinite(n) && n > 0);
  }
  const legacy = payment?.proof_of_payment_document;
  if (legacy == null || legacy === '') return [];
  const n = Number(legacy);
  return Number.isFinite(n) && n > 0 ? [n] : [];
};

/** The preview + download icon pair for one document. */
const DocActions = ({ docId, docActions, dense = false }) => (
  <>
    <Tooltip title="Preview">
      <IconButton size="small" onClick={() => docActions.openPreview(docId)} sx={dense ? { p: 0.25 } : undefined}>
        <VisibilityIcon fontSize="small" />
      </IconButton>
    </Tooltip>
    <Tooltip title="Download">
      <IconButton size="small" onClick={() => docActions.downloadDocument(docId)} sx={dense ? { p: 0.25 } : undefined}>
        <DownloadIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  </>
);

/** Popover body: resolves names lazily, only once opened. */
const ProofList = ({ ids, docActions }) => {
  const { documents, loading } = useDocumentSummaries(ids);
  const byId = new Map((documents || []).map((d) => [d.id, d]));

  return (
    <List dense sx={{ minWidth: 260, maxWidth: 420, py: 0.5 }}>
      {loading && (
        <ListItem>
          <CircularProgress size={16} sx={{ mr: 1 }} />
          <ListItemText primary="Loading documents…" />
        </ListItem>
      )}
      {!loading && ids.map((id, i) => (
        <ListItem
          key={id}
          secondaryAction={<DocActions docId={id} docActions={docActions} dense />}
        >
          <AttachFileIcon fontSize="small" color="action" sx={{ mr: 1 }} />
          <ListItemText
            primaryTypographyProps={{ variant: 'body2', noWrap: true, title: byId.get(id)?.file_name || undefined }}
            primary={byId.get(id)?.file_name || `Document ${i + 1}`}
          />
        </ListItem>
      ))}
    </List>
  );
};

/**
 * Proof-of-payment cell for the matched-payment tables.
 *
 * T3-129: a payment can carry several proofs. Rendering is deliberately tiered so the common
 * case does not change:
 *   0 documents → the existing "-"
 *   1 document  → the SAME preview/download icon pair as before (zero visual change)
 *   N documents → a count chip opening a popover with one row per document
 *
 * Document names are resolved ONLY when the popover opens. Calling useDocumentSummaries per row
 * would mean one HTTP request per row across three tables.
 */
const PaymentProofCell = ({ payment, ids: idsProp, docActions }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const ids = idsProp || toProofIds(payment);

  if (ids.length === 0) {
    return <Typography variant="caption" color="textSecondary">-</Typography>;
  }

  if (ids.length === 1) {
    return <DocActions docId={ids[0]} docActions={docActions} />;
  }

  return (
    <>
      <Tooltip title="View attached documents">
        <Chip
          size="small"
          variant="outlined"
          icon={<AttachFileIcon />}
          label={`${ids.length} docs`}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ cursor: 'pointer' }}
        />
      </Tooltip>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ px: 1, pt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Proof of payment ({ids.length})
          </Typography>
        </Box>
        <ProofList ids={ids} docActions={docActions} />
      </Popover>
    </>
  );
};

/**
 * Read-only attachment list for the match dialog's view / reapportion modes.
 *
 * Before T3-129 those modes showed NOTHING — the whole proof field was gated behind
 * `!lockMetadata`, so an operator looking at a matched payment could not see its own proof.
 * Read-only in both modes deliberately: reapportion posts to PUT /payments/:id/apportionment,
 * which does not touch the proof, so an upload control here would imply a mutation that never
 * happens.
 */
export const ExistingProofList = ({ ids, docActions, label = 'Proof of Payment' }) => {
  const { documents, loading } = useDocumentSummaries(ids);
  const byId = new Map((documents || []).map((d) => [d.id, d]));

  if (!Array.isArray(ids) || ids.length === 0) {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{label}</Typography>
        <Typography variant="body2" color="text.secondary">No documents attached</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label} ({ids.length})
      </Typography>
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <List dense sx={{ py: 0 }}>
          {ids.map((id, i) => (
            <ListItem key={id} secondaryAction={<DocActions docId={id} docActions={docActions} dense />}>
              <AttachFileIcon fontSize="small" color="action" sx={{ mr: 1 }} />
              <ListItemText
                primaryTypographyProps={{ variant: 'body2', noWrap: true, title: byId.get(id)?.file_name || undefined }}
                primary={loading ? 'Loading…' : (byId.get(id)?.file_name || `Document ${i + 1}`)}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );
};

export default PaymentProofCell;
